/**
 * Mock Prize API Service
 * 
 * Provides cryptographically secure prize selection for preview/testing modes.
 * Replicates production Prize API behavior using weighted random selection.
 * 
 * **Usage Context**:
 * - Portal preview (Experience Studio, Experience Preview Route)
 * - Game configurator dialogs
 * - Customer Journey Simulator
 * 
 * **NOT used in**:
 * - Experience app (uses real Prize API Cloud Function)
 * 
 * **Security**: Uses crypto.getRandomValues() for fairness (not Math.random())
 * 
 * @example
 * ```typescript
 * const prizes = [
 *   { name: 'Grand Prize', probability: 5 },
 *   { name: 'Second Prize', probability: 15 },
 *   { name: 'Third Prize', probability: 30 }
 * ];
 * 
 * const winningIndex = MockPrizeApiService.selectPrizeIndex(prizes);
 * console.log('Winner:', prizes[winningIndex].name);
 * ```
 */
import { Injectable } from '@angular/core';

/**
 * Prize item with probability weight
 * Flexible interface to support Prize, PrizeTemplate, or simple objects
 * Type-safe with explicit optional properties (no index signature)
 */
export interface PrizeItem {
  name?: string;
  label?: string;
  probability?: number;
  odds?: number;
  // Allow any other properties for flexibility
  id?: string;
  type?: string;
  value?: string; 
  description?: string;
  redemptionInstructions?: string;
  isActive?: boolean;
  templateId?: string;
  backgroundColor?: string;
  labelColor?: string;
  category?: string;
  imageUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MockPrizeApiService {
  /**
   * Select a prize index using weighted random selection with crypto RNG.
   * 
   * **Algorithm** (matches Cloud Functions `weightedRandom()`):
   * 1. Build cumulative weight array
   * 2. Generate cryptographically secure random number
   * 3. Find first weight >= random number
   * 
   * **Crypto RNG**:
   * - Browser: `crypto.getRandomValues()` (Web Crypto API)
   * - Node.js: Automatically uses secure RNG via TypedArray
   * 
   * @param prizes - Array of prize items with probability/odds weights
   * @returns Winning prize index (0-based)
   * @throws Error if prizes array is empty or probabilities are invalid
   * 
   * @example
   * ```typescript
   * const prizes = [
   *   { name: 'Prize 1', probability: 10 },
   *   { name: 'Prize 2', probability: 30 },
   *   { name: 'Prize 3', probability: 60 }
   * ];
   * 
   * const winningIndex = MockPrizeApiService.selectPrizeIndex(prizes);
   * // Returns 0, 1, or 2 with 10%, 30%, 60% probability respectively
   * ```
   */
  static selectPrizeIndex(prizes: PrizeItem[]): number {
    if (!prizes || prizes.length === 0) {
      throw new Error('[MockPrizeApi] No prizes available for selection');
    }

    // Build cumulative weights array
    const cumulativeWeights: number[] = [];
    let sum = 0;

    prizes.forEach((prize) => {
      // Support both 'probability' and 'odds' property names
      const weight = prize.probability ?? prize.odds ?? 1;
      
      if (weight < 0) {
        throw new Error(`[MockPrizeApi] Invalid probability/odds: ${weight} for prize: ${prize.name || prize.label}`);
      }
      
      sum += weight;
      cumulativeWeights.push(sum);
    });

    if (sum === 0) {
      throw new Error('[MockPrizeApi] Total probability/odds is 0, cannot select prize');
    }

    // Generate cryptographically secure random number in range [0, sum)
    const randomNumber = this.getSecureRandom() * sum;

    // Find winning prize using binary search for performance
    // (Linear search is fine for small arrays, but this scales better)
    let winnerIndex = 0;
    
    for (let i = 0; i < cumulativeWeights.length; i++) {
      if (randomNumber < cumulativeWeights[i]) {
        winnerIndex = i;
        break;
      }
    }

    return winnerIndex;
  }

  /**
   * Generate cryptographically secure random number in range [0, 1).
   * 
   * Uses Web Crypto API (`crypto.getRandomValues()`) which provides:
   * - Cryptographically strong random values
   * - Suitable for security-sensitive applications
   * - Better distribution than Math.random()
   * 
   * **Fallback**: If crypto API unavailable (old browsers), falls back to Math.random()
   * with console warning.
   * 
   * @returns Random number in range [0, 1)
   * 
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Crypto/getRandomValues
   */
  static getSecureRandom(): number {
    // Check if crypto API is available (modern browsers + Node.js 15+)
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      // Generate 32-bit unsigned integer
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      
      // Convert to float in range [0, 1)
      // Max value of Uint32: 4,294,967,295 (2^32 - 1)
      return array[0] / (0xFFFFFFFF + 1);
    }

    // Fallback for ancient browsers (should never happen in production)
    console.warn('[MockPrizeApi] crypto.getRandomValues() not available, falling back to Math.random()');
    return Math.random();
  }

  /**
   * Generate cryptographically secure random integer in range [min, max).
   * 
   * Useful for selecting array indices, generating IDs, etc.
   * 
   * @param min - Minimum value (inclusive)
   * @param max - Maximum value (exclusive)
   * @returns Random integer in range [min, max)
   * 
   * @example
   * ```typescript
   * const randomIndex = MockPrizeApiService.getSecureRandomInt(0, prizes.length);
   * ```
   */
  static getSecureRandomInt(min: number, max: number): number {
    if (min >= max) {
      throw new Error(`[MockPrizeApi] Invalid range: min (${min}) must be less than max (${max})`);
    }
    
    const range = max - min;
    return Math.floor(this.getSecureRandom() * range) + min;
  }

  /**
   * Create a weighted random callback function for game renderers.
   * 
   * **Usage Pattern**: Game renderers accept `getPrizeIndex?: () => Promise<number>`
   * callback for prize selection. This method creates that callback using crypto RNG.
   * 
   * @param prizes - Array of prize items with probability/odds weights
   * @returns Async callback that returns winning prize index
   * 
   * @example
   * ```typescript
   * const getPrizeIndex = MockPrizeApiService.createPrizeCallback(campaign.prizes);
   * 
   * // Pass to game renderer
   * await renderer.play({ getPrizeIndex });
   * ```
   */
  static createPrizeCallback(prizes: PrizeItem[]): () => Promise<number> {
    return async (): Promise<number> => {
      const index = this.selectPrizeIndex(prizes);
      console.log('[MockPrizeApi] Selected prize index:', index, 'Prize:', prizes[index]?.name || prizes[index]?.label);
      return index;
    };
  }
}
